import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ivrAPI, destinationsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const IVRFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditing);
  const [destinations, setDestinations] = useState([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      extension: '',
      name: '',
      description: '',
      timeout: 10,
      invalid_retries: 3,
      option_0: '', option_1: '', option_2: '', option_3: '', option_4: '',
      option_5: '', option_6: '', option_7: '', option_8: '', option_9: '',
    },
  });

  useEffect(() => {
    fetchDestinations();
    if (isEditing) fetchIVR();
  }, [id]);

  const fetchDestinations = async () => {
    try {
      const response = await destinationsAPI.getAll();
      setDestinations(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch destinations');
    }
  };

  const fetchIVR = async () => {
    try {
      const response = await ivrAPI.getById(id);
      const data = response.data?.data || response.data;
      reset(data);
    } catch (error) {
      toast.error('Failed to load IVR');
      navigate('/ivr');
    } finally {
      setIsFetching(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (isEditing) {
        await ivrAPI.update(id, data);
        toast.success('IVR updated');
      } else {
        await ivrAPI.create(data);
        toast.success('IVR created');
      }
      navigate('/ivr');
    } catch (error) {
      toast.error('Failed to save IVR');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-2xl animate-fade-in">
      <button onClick={() => navigate('/ivr')} className="flex items-center text-slate-400 hover:text-white mb-4">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />Back to IVR
      </button>
      <h1 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Edit' : 'Create'} IVR</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">Basic Info</h2>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="label">Extension *</label>
              <input {...register('extension', { required: true })} disabled={isEditing} className="input" placeholder="5000" />
            </div>
            <div>
              <label className="label">Name *</label>
              <input {...register('name', { required: true })} className="input" placeholder="Main Menu" />
            </div>
          </div>
          <div>
            <label className="label">Timeout (seconds)</label>
            <input type="number" {...register('timeout')} className="input" />
          </div>
        </div>

        <div className="card space-y-5">
          <h2 className="text-lg font-semibold text-white border-b border-slate-800 pb-3">Menu Options</h2>
          <p className="text-sm text-slate-400">Configure destinations for each keypress (0-9)</p>
          <div className="grid grid-cols-2 gap-4">
            {[0,1,2,3,4,5,6,7,8,9].map(num => (
              <div key={num}>
                <label className="label">Press {num}</label>
                <select {...register(`option_${num}`)} className="input">
                  <option value="">-- Not configured --</option>
                  {destinations.map(dest => (
                    <option key={dest.value || dest.id} value={dest.value || dest.id}>{dest.label || dest.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/ivr')} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IVRFormPage;
